import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { FiInbox, FiSearch, FiFilter, FiExternalLink, FiGlobe, FiMail, FiPhone } from 'react-icons/fi';
import { format } from 'date-fns';

export default function AdminLeads() {
  const { adminUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await adminApi.getRecentLeads(adminUser.admin_session_token);
        if (res.success) setLeads(res.leads || []);
      } catch (e) {
        console.error('Error fetching leads:', e);
      } finally { 
        setLoading(false); 
      }
    };
    fetchLeads();
  }, [adminUser]);

  const filtered = leads.filter(l => 
    `${l.client_business_name} ${l.visitor_name} ${l.visitor_email} ${l.visitor_phone}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="animate-pulse space-y-8"><div className="h-10 w-48 bg-slate-200 rounded-lg"></div><div className="h-[500px] bg-white rounded-[2.5rem]"></div></div>;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Central Lead Vault</h1>
        <p className="text-slate-500 font-bold text-sm mt-1 uppercase tracking-widest opacity-60">System-wide Archive</p>
      </header>

      <div className="bg-white rounded-[1.5rem] p-3 shadow-sm border border-slate-100 flex items-center gap-3">
        <div className="flex-grow relative group">
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-300 group-focus-within:text-brand-blue transition-colors">
            <SafeIcon icon={FiSearch} />
          </div>
          <input 
            type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search all leads by client, visitor name, or contact details..." 
            className="w-full pl-12 pr-4 py-3.5 bg-slate-50 rounded-xl text-xs font-bold outline-none border-2 border-transparent focus:border-brand-blue/10 transition-all"
          />
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1100px]">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="py-6 px-8 text-[10px] font-black text-slate-400 uppercase tracking-widest">Submission</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Business Client</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Visitor Info</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Message</th>
                <th className="py-6 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Alerts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(lead => (
                <tr key={lead.lead_id} className="group hover:bg-slate-50/80 transition-all">
                  <td className="py-6 px-8">
                    <div className="flex flex-col">
                      <span className="text-xs font-black text-slate-900">{format(new Date(lead.submitted_at), 'dd MMM yyyy')}</span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">{format(new Date(lead.submitted_at), 'h:mm a')}</span>
                    </div>
                  </td>
                  <td className="py-6 px-6">
                    <span className="text-xs font-black text-brand-blue">{lead.client_business_name}</span>
                  </td>
                  <td className="py-6 px-6">
                    <div className="flex flex-col">
                       <span className="text-xs font-black text-slate-900">{lead.visitor_name}</span>
                       <span className="text-[10px] font-bold text-slate-400">{lead.visitor_email}</span>
                    </div>
                  </td>
                  <td className="py-6 px-6">
                    <p className="text-xs text-slate-500 max-w-[300px] truncate">"{lead.message}"</p>
                  </td>
                  <td className="py-6 px-6">
                    <div className="flex justify-center gap-1.5">
                       <div className={`w-2 h-2 rounded-full ${lead.business_email_sent ? 'bg-emerald-400' : 'bg-slate-200'}`} title="Email Sent"></div>
                       <div className={`w-2 h-2 rounded-full ${lead.sms_sent ? 'bg-emerald-400' : 'bg-slate-200'}`} title="SMS Sent"></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}